import { useLang } from "@/i18n/LanguageContext";
import { deriveFlag } from "@/lib/resParser";
import { motion } from "framer-motion";
import AskAndyChip from "@/components/chat/AskAndyChip";

function buildPrefill(template, b) {
  return template
    .replace("{name}", b.name)
    .replace("{value}", b.value || "—")
    .replace("{unit}", b.unit ? ` ${b.unit}` : "")
    .replace(
      "{range}",
      b.referenceRange ||
        (b.referenceMin != null && b.referenceMax != null
          ? `${b.referenceMin}-${b.referenceMax}`
          : "—")
    );
}

export default function BiomarkerCard({ b, idx = 0 }) {
  const { t } = useLang();
  const flag = deriveFlag(b);

  let marker = 50; // default mid
  let outsideSide = null; // "low" | "high" | null
  if (b.numericValue != null && b.referenceMin != null && b.referenceMax != null && b.referenceMax > b.referenceMin) {
    const span = b.referenceMax - b.referenceMin;
    const padded = span * 0.4; // visual buffer on both sides
    const vmin = b.referenceMin - padded;
    const vmax = b.referenceMax + padded;
    const pos = ((b.numericValue - vmin) / (vmax - vmin)) * 100;
    marker = Math.max(2, Math.min(98, pos));
    if (b.numericValue < b.referenceMin) outsideSide = "low";
    if (b.numericValue > b.referenceMax) outsideSide = "high";
  }
  const refStart = 20;
  const refEnd = 80;

  const tone =
    flag === "normal"
      ? { dot: "bg-emerald-500", text: "text-emerald-700", label: t.results.biomarkers.normal }
      : flag === "high"
      ? { dot: "bg-amber-500", text: "text-amber-700", label: t.results.biomarkers.high }
      : flag === "low"
      ? { dot: "bg-sky-500", text: "text-sky-700", label: t.results.biomarkers.low }
      : { dot: "bg-zinc-400", text: "text-zinc-600", label: "—" };

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.3) }}
      className="rounded-2xl border border-zinc-200 bg-white p-5 transition hover:border-zinc-300"
      data-testid={`biomarker-card-${b.code || b.name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-900">{b.name}</p>
          {b.code && (
            <p className="mt-0.5 font-mono text-[10px] uppercase text-zinc-400">{b.code}</p>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full bg-zinc-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${tone.text}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
          {tone.label}
        </span>
      </div>

      <div className="mt-4 flex items-baseline gap-2 font-mono">
        <span className="text-2xl font-semibold text-zinc-900">{b.value || "—"}</span>
        {b.unit && <span className="text-xs text-zinc-400">{b.unit}</span>}
      </div>

      {(b.referenceMin != null || b.referenceMax != null || b.referenceRange) && (
        <div className="mt-4">
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-100">
            {/* reference band */}
            <div
              className="absolute top-0 h-full rounded-full bg-emerald-100"
              style={{ left: `${refStart}%`, width: `${refEnd - refStart}%` }}
            />
            {/* low/high tints */}
            <div
              className="absolute top-0 h-full bg-sky-100"
              style={{ left: 0, width: `${refStart}%` }}
            />
            <div
              className="absolute top-0 h-full bg-amber-100"
              style={{ left: `${refEnd}%`, width: `${100 - refEnd}%` }}
            />
            {/* marker */}
            {b.numericValue != null && (
              <div
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${marker}%` }}
              >
                <div
                  className={`h-3.5 w-3.5 rounded-full border-2 border-white shadow ${
                    outsideSide === "high"
                      ? "bg-amber-500"
                      : outsideSide === "low"
                      ? "bg-sky-500"
                      : "bg-[color:var(--sentinel)]"
                  }`}
                />
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between font-mono text-[10px] text-zinc-400">
            <span>{b.referenceMin != null ? b.referenceMin : ""}</span>
            <span className="uppercase tracking-[0.18em]">{t.results.biomarkers.ref}</span>
            <span>{b.referenceMax != null ? b.referenceMax : ""}</span>
          </div>
        </div>
      )}

      {!b.isNormal && (
        <div className="mt-4 flex justify-end">
          <AskAndyChip
            size="xs"
            prefill={buildPrefill(t.chat.prefill.biomarker, b)}
            testId={`ask-andy-biomarker-${b.code || b.name}`}
          />
        </div>
      )}
    </motion.article>
  );
}
